<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import AddLabel from './AddLabel.vue';
import EditLabel from './EditLabel.vue';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import SettingsTable from 'dashboard/components-next/Settings/SettingsTable.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedLabel = ref({});

const records = computed(() => getters['labels/getLabels'].value);
const uiFlags = computed(() => getters['labels/getUIFlags'].value);

const deleteMessage = computed(() => ` ${selectedLabel.value.title}?`);

const tableHeaders = computed(() => [
  t('LABEL_MGMT.LIST.TABLE_HEADER.NAME'),
  t('LABEL_MGMT.LIST.TABLE_HEADER.DESCRIPTION'),
  t('LABEL_MGMT.LIST.TABLE_HEADER.COLOR'),
  '',
]);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedLabel.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedLabel.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteLabel = async id => {
  try {
    await store.dispatch('labels/delete', id);
    useAlert(t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedLabel.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedLabel.value.id] = true;
  closeDeletePopup();
  deleteLabel(selectedLabel.value.id);
};

onBeforeMount(() => {
  store.dispatch('labels/get');
});
</script>

<template>
  <SettingsPageLayout
    :title="$t('LABEL_MGMT.HEADER')"
    :description="$t('LABEL_MGMT.DESCRIPTION')"
  >
    <template #actions>
      <Button
        icon="i-lucide-circle-plus"
        :label="$t('LABEL_MGMT.HEADER_BTN_TXT')"
        @click="openAddPopup"
      />
    </template>

    <SettingsTable
      :headers="tableHeaders"
      :is-loading="uiFlags.isFetching"
      :is-empty="!records.length"
      :empty-message="$t('LABEL_MGMT.LIST.404')"
    >
      <tr
        v-for="(label, index) in records"
        :key="label.title"
        class="hover:bg-n-solid-3 transition-colors"
      >
        <td class="px-5 py-3">
          <span class="text-sm font-medium text-n-slate-12 break-words">
            {{ label.title }}
          </span>
        </td>
        <td class="px-5 py-3">
          <span class="text-sm text-n-slate-11">
            {{ label.description }}
          </span>
        </td>
        <td class="px-5 py-3">
          <div class="flex items-center gap-2">
            <span
              class="w-4 h-4 rounded border border-n-weak shrink-0"
              :style="{ backgroundColor: label.color }"
            />
            <span class="text-sm text-n-slate-11">{{ label.color }}</span>
          </div>
        </td>
        <td class="px-5 py-3">
          <div class="flex gap-1 justify-end">
            <Button
              v-tooltip.top="$t('LABEL_MGMT.FORM.EDIT')"
              icon="i-lucide-pen"
              slate
              xs
              faded
              @click="openEditPopup(label)"
            />
            <Button
              v-tooltip.top="$t('LABEL_MGMT.FORM.DELETE')"
              icon="i-lucide-trash-2"
              xs
              ruby
              faded
              :is-loading="loading[label.id]"
              @click="openDeletePopup(label, index)"
            />
          </div>
        </td>
      </tr>
    </SettingsTable>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddLabel @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditLabel :selected-response="selectedLabel" @close="hideEditPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('LABEL_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('LABEL_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('LABEL_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsPageLayout>
</template>
